import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateOrderStatus, updateDynamicColumn } from '../redux/columnsSlice';
import axios from 'axios';
import { buildApiUrl } from '../utils/api';
import SimpleModal from './SimpleModal';
import { jsPDF } from "jspdf";

const EditableCell = ({ info, columnConfig, unique_key, aliasMapping }) => {
  const dispatch = useDispatch();
  const updateRowLoading = useSelector((state) => state.columns.updateRowLoading);
  const updateRowError = useSelector((state) => state.columns.updateRowError);
  const orderStatuses = useSelector((state) => state.columns.orderStatuses);

  // Estados generales
  const [value, setValue] = useState(info.getValue() ?? "");
  const [localError, setLocalError] = useState(null);

  // Estados y lógica para envío de correo
  const [rowData, setRowData] = useState({});
  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldsWithAliases, setFieldsWithAliases] = useState([]);
  const [isFieldSelectorModalOpen, setIsFieldSelectorModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    destinatario: value,
    asunto: '',
    mensaje: '',
  });

  // Estado para manejo de WhatsApp
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState("");

  // Sincroniza el valor al renderizar o actualizar la celda
  useEffect(() => {
    setValue(info.getValue() ?? "");
  }, [info, info.getValue()]);

  const handleChange = (e) => {
    const newValue =
      columnConfig.column_type === 'Boolean' ? e.target.checked : e.target.value;
    setValue(newValue);
  };

  const handleBlur = () => {
    setLocalError(null);
    if (columnConfig.column_type === 'Número' && isNaN(value)) {
      setLocalError("Debe ser un número válido.");
      setValue(info.getValue());
      return;
    }
    // Evitar re-disparar el update si el valor no cambió
    if (value === info.getValue() && columnConfig.column_type !== 'Estado del Pedido') {
      return;
    }
    // No actualizar desde aquí si es "Estado del Pedido"
    if (columnConfig.column_type === 'Estado del Pedido') {
      return;
    }
    // No actualizar si es "Correo" (se maneja con el botón de enviar)
    if (columnConfig.column_type !== 'Correo') {
      dispatch(
        updateDynamicColumn({
          unique_key,
          order_id: info.row.original.order_id,
          column_name: columnConfig.column_name,
          new_value: value,
        })
      ).catch((err) => console.error("Error al actualizar el valor:", err));
    }
  };

  const handleSelectChange = (e) => {
    const newValue = e.target.value;
    if (columnConfig.column_type === 'Estado del Pedido') {
      setValue("⌛ Guardando...");
      dispatch(
        updateOrderStatus({
          unique_key,
          order_id: info.row.original.order_id,
          new_status: newValue,
        })
      )
        .unwrap()
        .then((res) => {
          setTimeout(() => setValue(res.updatedRow.status), 500);
        })
        .catch(() => {
          setValue(info.getValue() ?? "");
        });
    } else {
      dispatch(
        updateDynamicColumn({
          unique_key,
          order_id: info.row.original.order_id,
          column_name: columnConfig.column_name,
          new_value: newValue,
        })
      )
        .then(() => setValue(newValue))
        .catch((err) => console.error("Error al actualizar valor dropdown:", err));
    }
  };

  // Función para agregar pie de página en cada página del PDF
  const addFooter = (doc) => {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Página ${i} de ${pageCount}`, 10.75, 13.5, { align: 'center' });
      doc.text("Nombre de la Empresa - Dirección", 10.75, 13.9, { align: 'center' });
    }
  };

  // Función para generar PDF con datos agrupados (por order_id)
  const generatePDF = (order) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'cm',
      format: [21.5, 14],
    });

// Imagen de fondo a página completa (reemplaza con tu cadena Base64 válida)
const backgroundBase64 = 'data:image/png;base64,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'; // Reemplaza con tu cadena base64 completa
doc.addImage(backgroundBase64, 'PNG', 0, 0, 14, 5);

    const {
      order_id,
      order_date,
      nombre_completo_cliente,
      ciudad,
      billing_phone,
      direccion,
      estado,
      codigo_postal,
      post_excerpt,
      paqueteria,
      guia_paqueteria,
      product_name,
      quantity,
      precio_unitario,
      order_total,
      productos,
    } = order;

    const offsetX = 2;
    const offsetY = 2;
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    let y = 2.5;

    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text("Salida de Materiales", 1 + offsetX, y + offsetY);
    y += 1;
    doc.setFontSize(8);

    doc.setFont(undefined, 'bold');
    doc.text("FECHA DEL PEDIDO:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(order_date || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.6;

    doc.setFont(undefined, 'bold');
    doc.text("CLIENTE:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(nombre_completo_cliente || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.6;

    doc.setFont(undefined, 'bold');
    doc.text("REGIÓN:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(ciudad || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.6;

    doc.setFont(undefined, 'bold');
    doc.text("TEL. CLIENTE:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(billing_phone || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.6;

    doc.setFont(undefined, 'bold');
    doc.text("PEDIDO:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(order_id || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.8;

    if (productos && Array.isArray(productos)) {
      doc.setFont(undefined, 'bold');
      doc.text("PRODUCTOS:", 1 + offsetX, y + offsetY);
      y += 0.8;
      productos.forEach((prod, index) => {
        doc.setFont(undefined, 'normal');
        doc.text(
          `${index + 1}. ${prod.product_name} - Cantidad: ${prod.quantity}, Precio Unitario: ${prod.precio_unitario}, Total: ${prod.order_total}`,
          1 + offsetX,
          y + offsetY
        );
        y += 0.6;
      });
    } else {
      doc.setFont(undefined, 'bold');
      doc.text("CANTIDAD:", 3 + offsetX, y + offsetY, { align: 'right' });
      doc.setFont(undefined, 'normal');
      doc.text(quantity || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
      y += 0.6;

      doc.setFont(undefined, 'bold');
      doc.text("PRODUCTO:", 3 + offsetX, y + offsetY, { align: 'right' });
      doc.setFont(undefined, 'normal');
      doc.text(product_name || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
      y += 0.6;

      doc.setFont(undefined, 'bold');
      doc.text("PRECIO UNITARIO:", 3 + offsetX, y + offsetY, { align: 'right' });
      doc.setFont(undefined, 'normal');
      doc.text(precio_unitario ? String(precio_unitario) : '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
      y += 0.6;

      doc.setFont(undefined, 'bold');
      doc.text("TOTAL:", 3 + offsetX, y + offsetY, { align: 'right' });
      doc.setFont(undefined, 'normal');
      doc.text(order_total ? String(order_total) : '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
      y += 0.6;
    }

    doc.setFont(undefined, 'bold');
    doc.text("DIRECCIÓN DE ENTREGA:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(direccion || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.6;

    doc.setFont(undefined, 'bold');
    doc.text("ESTADO:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(estado || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.6;

    doc.setFont(undefined, 'bold');
    doc.text("CÓDIGO POSTAL:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(codigo_postal || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.6;

    doc.setFont(undefined, 'bold');
    doc.text("NOTAS DEL CLIENTE:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(post_excerpt || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.6;

    doc.setFont(undefined, 'bold');
    doc.text("PAQUETERÍA:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(paqueteria || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.6;

    doc.setFont(undefined, 'bold');
    doc.text("GUÍA DE ENTREGA:", 3 + offsetX, y + offsetY, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.text(guia_paqueteria || '-', 3.2 + offsetX, y + offsetY, { align: 'left' });
    y += 0.6;

// Agregar imagen en el footer en lugar de datos de texto
const footerImageBase64 = 'data:image/png;base64,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'; // Reemplaza con la cadena Base64 de tu imagen de footer
doc.addImage(footerImageBase64, 'PNG', 0, 16.5, 14, 5);

doc.save(`${nombre_completo_cliente || 'Cliente'}.pdf`);
};

  // Función para parsear las opciones del Dropdown
  const options = (() => {
    if (typeof columnConfig.options === 'string') {
      try {
        return JSON.parse(columnConfig.options);
      } catch {
        return [];
      }
    }
    return Array.isArray(columnConfig.options)
      ? columnConfig.options
      : [];
  })();

  // Función para enviar correo
  const handleSendEmail = useCallback(() => {
    axios
      .post(buildApiUrl('/send_email'), {
        destinatario: emailForm.destinatario,
        asunto: emailForm.asunto,
        mensaje: emailForm.mensaje,
      })
      .then(() => {
        alert("Correo enviado exitosamente.");
        setIsEmailModalOpen(false);
        setEmailForm({ destinatario: '', asunto: '', mensaje: '' });
      })
      .catch((err) => {
        alert("Error al enviar correo: " + (err.response?.data || err.message));
      });
  }, [emailForm]);

  // Función para abrir WhatsApp (abre en nueva pestaña)
  const openWhatsApp = useCallback(() => {
    const formattedNumber = value.replace(/\D/g, '');
    if (!formattedNumber) return;
    const url = `https://web.whatsapp.com/send?phone=${formattedNumber}&text=Saludos, nos comunicamos... .`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [value]);

  // Función que retorna el contenido editable según el tipo de columna
  const renderEditableInput = () => {
    switch (columnConfig.column_type) {
      case 'Salida de Materiales':
        return (
          <div 
            className="editable-cell-container" 
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-start', 
              marginTop: '10px' 
            }}
          >
            <input
              type="text"
              className="autocrm-input"
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={updateRowLoading}
              maxLength={columnConfig.max_length || undefined}
              style={{ display: 'none' }}
            />
            <div style={{ marginTop: '8px' }}>
              <button
                className="autocrm-btn autocrm-btn-primary"
                onClick={() => {
                  const currentRowData = info.row?.original || {};
                  generatePDF(currentRowData);
                }}
                disabled={updateRowLoading}
              >
                Generar PDF
              </button>
            </div>
          </div>
        );

      case 'Teléfono | Whatsapp':
        return (
          <div 
            className="editable-cell-container" 
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
          >
            <input
              type="text"
              className="autocrm-input"
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={updateRowLoading}
              maxLength={columnConfig.max_length || undefined}
              style={{ width: '100%' }}
            />
            <div style={{ marginTop: '4px', display: 'flex', gap: '8px' }}>
              <a
                href={`tel:${value}`}
                style={{
                  backgroundColor: '#fafafa',
                  color: '#fff',
                  padding: '5px 8px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                📞
              </a>
              <button
                onClick={openWhatsApp}
                style={{
                  backgroundColor: '#25D366',
                  color: '#fff',
                  padding: '5px 8px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                💬 WhatsApp
              </button>
            </div>
          </div>
        );

      case 'Número':
        return (
          <input
            type="number"
            className="autocrm-input"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={updateRowLoading}
          />
        );

      case 'Fecha':
        return (
          <input
            type="date"
            className="autocrm-input"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={updateRowLoading}
          />
        );

      case 'Hora':
        return (
          <input
            type="time"
            className="autocrm-input"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={updateRowLoading}
          />
        );

      case 'Texto Multilínea':
        return (
          <textarea
            className="autocrm-input"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={updateRowLoading}
            maxLength={columnConfig.max_length || undefined}
          />
        );

      case 'Dropdown':
        return (
          <select
            className="autocrm-select"
            value={value}
            onChange={handleSelectChange}
            disabled={updateRowLoading}
          >
            <option value="">Seleccionar</option>
            {options.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'Estado del Pedido':
        return (
          <select
            className="autocrm-select"
            value={value}
            onChange={handleSelectChange}
            disabled={updateRowLoading}
          >
            <option value="">Seleccionar</option>
            {orderStatuses &&
              Object.entries(orderStatuses).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
          </select>
        );

      case 'Boolean':
        return (
          <input
            type="checkbox"
            className="autocrm-checkbox"
            checked={!!value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={updateRowLoading}
          />
        );

      case 'Correo':
        return (
          <div
            className="autocrm-correo-config"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              marginTop: '10px'
            }}
          >
            <input
              type="text"
              className="autocrm-input"
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={updateRowLoading}
              maxLength={columnConfig.max_length || undefined}
              style={{ width: '100%' }}
            />
            <div style={{ marginTop: '8px' }}>
              <button
                className="autocrm-btn autocrm-btn-primary"
                onClick={() => {
                  const currentRowData = info.row?.original || {};
                  const computedFieldsWithAliases = Object.keys(currentRowData).map((key) => ({
                    key,
                    label: aliasMapping ? aliasMapping[key] || key : key,
                  }));
                  setRowData(currentRowData);
                  setFieldsWithAliases(computedFieldsWithAliases);
                  setSelectedFields(Object.keys(currentRowData));
                  setIsFieldSelectorModalOpen(true);
                }}
                disabled={updateRowLoading}
              >
                {value ? `Enviar correo a ${value}` : 'Enviar Correo'}
              </button>
            </div>
          </div>
        );

      // Nuevo case para "Cantidad Real de Piezas"
      case 'Cantidad Real de Piezas':
        const productId = info.row.original.product_id;
        const cantidad = parseFloat(info.row.original.quantity || 0);
        const piezasPorPaquete = (() => {
          try {
            const config = Array.isArray(columnConfig.options)
              ? columnConfig.options
              : JSON.parse(columnConfig.options || '[]');
            const found = config.find((item) => String(item.product_id) === String(productId));
            return found ? parseFloat(found.piezas_por_paquete || 0) : 0;
          } catch (err) {
            console.warn("❌ Error al leer opciones en Cantidad Real de Piezas:", err);
            return 0;
          }
        })();
        const cantidadTotal = cantidad * piezasPorPaquete;
        return (
          <span style={{ fontWeight: 'bold' }}>
            {isNaN(cantidadTotal) ? '-' : cantidadTotal}
          </span>
        );

      default:
        return (
          <input
            type="text"
            className="autocrm-input"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={updateRowLoading}
            maxLength={columnConfig.max_length || undefined}
          />
        );
    }
  };

  return (
    <div className="editable-cell-container">
      {columnConfig.is_editable ? (
        renderEditableInput()
      ) : (
        <span>{value || columnConfig.default_value || '-'}</span>
      )}
      {localError && <div className="error-message">{localError}</div>}
      {updateRowError && <div className="error-message">{updateRowError}</div>}

      {/* Modal para seleccionar campos a copiar para el correo */}
      {isFieldSelectorModalOpen && (
        <SimpleModal
          isOpen={isFieldSelectorModalOpen}
          onClose={() => setIsFieldSelectorModalOpen(false)}
        >
          <h2 className="autocrm-modal-title">Seleccionar campos a copiar</h2>
          <div className="autocrm-filas-correo">
            {fieldsWithAliases.map(({ key, label }) => (
              <div key={key} className="autocrm-modal-field-item">
                <input
                  type="checkbox"
                  checked={selectedFields.includes(key)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedFields((prev) => [...prev, key]);
                    } else {
                      setSelectedFields((prev) =>
                        prev.filter((item) => item !== key)
                      );
                    }
                  }}
                />
                <label>{label}</label>
              </div>
            ))}
          </div>
          <div className="autocrm-modal-buttons">
            <button
              className="autocrm-btn"
              onClick={() => {
                const mensaje = fieldsWithAliases
                  .filter((field) => selectedFields.includes(field.key))
                  .map((field) => `${field.label}: ${rowData[field.key]}`)
                  .join('\n');
                setEmailForm({
                  destinatario: value,
                  asunto: rowData.id
                    ? `Datos del registro ${rowData.id}`
                    : 'Datos del registro',
                  mensaje,
                });
                setIsFieldSelectorModalOpen(false);
                setIsEmailModalOpen(true);
              }}
            >
              OK
            </button>
          </div>
        </SimpleModal>
      )}

      {/* Modal para envío de correo */}
      {isEmailModalOpen && (
        <SimpleModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
        >
          <h2>Enviar Correo</h2>
          <div className="autocrm-email-field">
            <label className="autocrm-label">Destinatario:</label>
            <input
              type="email"
              className="autocrm-input"
              value={emailForm.destinatario}
              onChange={(e) =>
                setEmailForm({ ...emailForm, destinatario: e.target.value })
              }
              style={{ width: '100%' }}
            />
          </div>
          <div className="autocrm-email-field">
            <label className="autocrm-label">Asunto:</label>
            <input
              type="text"
              className="autocrm-input"
              value={emailForm.asunto}
              onChange={(e) =>
                setEmailForm({ ...emailForm, asunto: e.target.value })
              }
              style={{ width: '100%' }}
            />
          </div>
          <div className="autocrm-email-field">
            <label className="autocrm-label">Mensaje:</label>
            <textarea
              className="autocrm-input autocrm-textarea"
              value={emailForm.mensaje}
              onChange={(e) =>
                setEmailForm({ ...emailForm, mensaje: e.target.value })
              }
              style={{ width: '100%', height: '100px' }}
            />
          </div>
          <button className="autocrm-btn" onClick={handleSendEmail}>
            Enviar
          </button>
        </SimpleModal>
      )}
    </div>
  );
};

export default React.memo(EditableCell);
